import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ResourceLibrary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'implementation', label: 'Implementation Guides' },
    { value: 'roi-analysis', label: 'ROI Analysis' },
    { value: 'curriculum', label: 'Curriculum Development' },
    { value: 'case-studies', label: 'Case Studies' },
    { value: 'industry-reports', label: 'Industry Reports' },
    { value: 'best-practices', label: 'Best Practices' }
  ];

  const resourceTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'whitepaper', label: 'Whitepapers' },
    { value: 'guide', label: 'Implementation Guides' },
    { value: 'case-study', label: 'Case Studies' },
    { value: 'report', label: 'Industry Reports' },
    { value: 'template', label: 'Templates' },
    { value: 'checklist', label: 'Checklists' }
  ];

  const resources = [
    {
      id: 1,
      title: "AI Education Implementation Roadmap",
      description: "Comprehensive guide for integrating AI curriculum into existing programs with timeline and milestones.",
      category: "implementation",
      type: "guide",
      downloadCount: 2847,
      pages: 45,
      fileSize: "2.3 MB",
      format: "PDF",
      featured: true,
      tags: ["AI", "Curriculum", "Implementation", "Higher Education"]
    },
    {
      id: 2,
      title: "ROI Analysis: AI Training Programs in Higher Education",
      description: "Detailed analysis of return on investment for AI education programs with real data from 50+ institutions.",
      category: "roi-analysis",
      type: "whitepaper",
      downloadCount: 1923,
      pages: 28,
      fileSize: "1.8 MB",
      format: "PDF",
      featured: true,
      tags: ["ROI", "Analysis", "Higher Education", "Metrics"]
    },
    {
      id: 3,
      title: "Stanford University: AI Curriculum Transformation",
      description: "Complete case study of how Stanford transformed their computer science program with AI integration.",
      category: "case-studies",
      type: "case-study",
      downloadCount: 3156,
      pages: 18,
      fileSize: "1.2 MB",
      format: "PDF",
      featured: false,
      tags: ["Stanford", "Case Study", "Transformation", "Success Story"]
    },
    {
      id: 4,
      title: "Corporate AI Training: Fortune 500 Best Practices",
      description: "Industry report analyzing successful AI training implementations across Fortune 500 companies.",
      category: "best-practices",
      type: "report",
      downloadCount: 1654,
      pages: 35,
      fileSize: "2.1 MB",
      format: "PDF",
      featured: false,
      tags: ["Corporate", "Fortune 500", "Best Practices", "Training"]
    },
    {
      id: 5,
      title: "Curriculum Development Template: Robotics Engineering",
      description: "Ready-to-use template for developing comprehensive robotics engineering curriculum.",
      category: "curriculum",
      type: "template",
      downloadCount: 987,
      pages: 12,
      fileSize: "0.8 MB",
      format: "DOCX",
      featured: false,
      tags: ["Robotics", "Template", "Curriculum", "Engineering"]
    },
    {
      id: 6,
      title: "Government AI Training: Security & Compliance Guide",
      description: "Specialized guide for implementing AI training in government agencies with security considerations.",
      category: "implementation",
      type: "guide",
      downloadCount: 743,
      pages: 32,
      fileSize: "1.9 MB",
      format: "PDF",
      featured: false,
      tags: ["Government", "Security", "Compliance", "AI Training"]
    },
    {
      id: 7,
      title: "Faculty Training Checklist: AI Education Readiness",
      description: "Comprehensive checklist to ensure faculty are prepared for AI curriculum delivery.",
      category: "best-practices",
      type: "checklist",
      downloadCount: 1234,
      pages: 8,
      fileSize: "0.5 MB",
      format: "PDF",
      featured: false,
      tags: ["Faculty", "Training", "Checklist", "Readiness"]
    },
    {
      id: 8,
      title: "Future of Work: AI Skills Gap Analysis 2024",
      description: "Industry report analyzing the current AI skills gap and future workforce requirements.",
      category: "industry-reports",
      type: "report",
      downloadCount: 2156,
      pages: 42,
      fileSize: "2.7 MB",
      format: "PDF",
      featured: true,
      tags: ["Future of Work", "Skills Gap", "2024", "Workforce"]
    }
  ];

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesType = selectedType === 'all' || resource.type === selectedType;
    
    return matchesSearch && matchesCategory && matchesType;
  });

  const featuredResources = filteredResources.filter(resource => resource.featured);

  const getTypeIcon = (type) => {
    const icons = {
      whitepaper: 'FileText',
      guide: 'BookOpen',
      'case-study': 'Award',
      report: 'BarChart3',
      template: 'Layout',
      checklist: 'CheckSquare'
    };
    return icons[type] || 'File';
  };

  const getTypeColor = (type) => {
    const colors = {
      whitepaper: 'text-accent',
      guide: 'text-success',
      'case-study': 'text-warning',
      report: 'text-cyber-purple',
      template: 'text-blue-400',
      checklist: 'text-green-400'
    };
    return colors[type] || 'text-muted-foreground';
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            Resource
            <span className="gradient-text"> Library</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Access comprehensive guides, case studies, and industry reports to support your AI/Robotics education implementation
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-12">
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <Input
              type="search"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="md:col-span-1"
            />
            
            <Select
              options={categories}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="Select category"
            />
            
            <Select
              options={resourceTypes}
              value={selectedType}
              onChange={setSelectedType}
              placeholder="Select type"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {filteredResources.length} of {resources.length} resources
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-muted-foreground">Sort by:</span>
              <select className="bg-card border border-border rounded-lg px-3 py-1 text-sm text-foreground">
                <option>Most Downloaded</option>
                <option>Newest First</option>
                <option>Title A-Z</option>
              </select>
            </div>
          </div>
        </div>

        {/* Featured Resources */}
        {featuredResources.length > 0 && (
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-foreground mb-8">Featured Resources</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredResources.map((resource) => (
                <div key={resource.id} className="holographic-card p-6 bg-card relative">
                  <div className="absolute top-4 right-4">
                    <div className="px-2 py-1 bg-accent text-white text-xs font-medium rounded-full">
                      Featured
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4 mb-4">
                    <div className={`w-12 h-12 rounded-lg bg-muted/20 flex items-center justify-center ${getTypeColor(resource.type)}`}>
                      <Icon name={getTypeIcon(resource.type)} size={24} />
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
                        {resource.title}
                      </h4>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
                        {resource.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                    <span>{resource.pages} pages</span>
                    <span>{resource.fileSize}</span>
                    <span>{resource.downloadCount.toLocaleString()} downloads</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {resource.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-muted/30 text-xs text-muted-foreground rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <Button
                    variant="default"
                    size="sm"
                    fullWidth
                    className="morphing-cta text-white font-semibold"
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download {resource.format}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Resources */}
        <div>
          <h3 className="text-2xl font-bold text-foreground mb-8">All Resources</h3>
          <div className="space-y-4">
            {filteredResources.map((resource) => (
              <div key={resource.id} className="holographic-card p-6 bg-card">
                <div className="flex items-start space-x-6">
                  <div className={`w-16 h-16 rounded-lg bg-muted/20 flex items-center justify-center ${getTypeColor(resource.type)} flex-shrink-0`}>
                    <Icon name={getTypeIcon(resource.type)} size={28} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-xl font-semibold text-foreground">{resource.title}</h4>
                      {resource.featured && (
                        <div className="px-2 py-1 bg-accent text-white text-xs font-medium rounded-full">
                          Featured
                        </div>
                      )}
                    </div>
                    
                    <p className="text-muted-foreground mb-4">{resource.description}</p>
                    
                    <div className="flex items-center space-x-6 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center space-x-1">
                        <Icon name="FileText" size={14} />
                        <span>{resource.pages} pages</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="HardDrive" size={14} />
                        <span>{resource.fileSize}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Download" size={14} />
                        <span>{resource.downloadCount.toLocaleString()} downloads</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {resource.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-muted/30 text-xs text-muted-foreground rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2 flex-shrink-0">
                    <Button
                      variant="default"
                      size="sm"
                      className="morphing-cta text-white font-semibold"
                      iconName="Download"
                      iconPosition="left"
                    >
                      Download
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      iconPosition="left"
                    >
                      Preview
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="holographic-card p-8 bg-card/50 backdrop-blur-md">
            <h3 className="text-2xl font-bold text-foreground mb-4">Need Custom Resources?</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Our team can create customized implementation guides, ROI analyses, and training materials specific to your organization's needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                className="morphing-cta text-white font-semibold"
                iconName="MessageCircle"
                iconPosition="left"
              >
                Request Custom Resources
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
              >
                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResourceLibrary;